import { useEffect, useRef } from 'react';

import { useCatalogContext } from '../../state/Catalog';
import { useCatalogRegistryTelemetry } from '../../Catalog.telemetry';

import { useGetCatalogProductDetailsLazyQuery, useGetCatalogProductDetailsQuery } from '../../api/queries/getCatalogProductDetails';
import { withWindow } from '@shared/utils/withWindow';
import { useShopStorefrontContext } from '../../components/ShopStorefrontProvider';
import type { GetCatalogProductDetailsQuery } from '../../api/queries/getCatalogProductDetails/getCatalogProductDetails.graphql.generated';
import { CatalogStorefront } from '@graphql/generated-base';
import { useRegistryShopping } from '@apps/registry/admin/RegistryShopping.provider';

export const useCatalogProductDetails = ({ catalogItemId }: { catalogItemId: string }) => {
  const { catalogProductDetailsQueryError } = useCatalogRegistryTelemetry();

  const { changeStorefront, isBabyStorefront } = useShopStorefrontContext();
  const { isEnabled: isShoppingEnabled } = useRegistryShopping();
  const { isAdminView, addCatalogItemToRegistry, addCatalogItemLoading, selectedCategory, isProductInRegistry } = useCatalogContext();

  const { product, error, loading } = useGetCatalogProductDetailsQuery({
    catalogItemId,
    withPdpSections: true,
    withProductRating: true,
    withInventory: isShoppingEnabled,
    queryOptions: {
      batchMode: 'fast',
      onCompleted(data: GetCatalogProductDetailsQuery) {
        const productSubcategory = data.productCatalogItemById?.subCategories.at(-1);
        // 1. Only handle this scenario in public shop where users can toggle storefronts
        // 2. The following only happens when a user is on a PDP page and refreshes the browser tab,
        //    losing track of the selected category
        if (!isAdminView && productSubcategory && !selectedCategory) {
          const shouldBeViewingBabyStorefront = productSubcategory.publicUrlPath.startsWith('/shop/baby/catalog');
          if (shouldBeViewingBabyStorefront && !isBabyStorefront) {
            changeStorefront(CatalogStorefront.baby);
          }
        }
      },
    },
  });

  const {
    sendRequest: getCatalogProductDetails,
    product: productDetails,
    loading: productDetailsLoading,
  } = useGetCatalogProductDetailsLazyQuery({
    catalogItemId,
    queryOptions: {
      batchMode: 'fast',
    },
  });

  useEffect(() => {
    if (error && catalogItemId) {
      catalogProductDetailsQueryError({ catalogItemId, error });
    }
  }, [catalogProductDetailsQueryError, catalogItemId, error]);

  const hasScrolledToTop = useRef(false);

  if (!hasScrolledToTop.current) {
    // only scroll to the top once, doing this outside of a use effect because we want it to happen instantaneously
    hasScrolledToTop.current = true;
    withWindow(window => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    });
  }

  return {
    addCatalogItemToRegistry,
    addCatalogItemLoading,
    hasError: !!error,
    productLoading: productDetailsLoading || loading,
    product: productDetails || product || null,
    error,
    getCatalogProductDetails,
    isProductInRegistry,
  };
};
